import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useUsersMap, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Plus, ClipboardText, PencilSimple, Trash, CalendarBlank, UserCircle, ArrowBendUpLeft, PaperPlaneRight, ArrowRight, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "done"];
const PRIORITIES = ["low", "medium", "high"];
const empty = { title: "", description: "", assigned_to: "", priority: "medium", status: "open", due_date: "" };

const PRIO_STYLE = {
  high: { bar: "bg-red-500", chip: "bg-red-50 text-red-600 ring-red-200" },
  medium: { bar: "bg-amber-500", chip: "bg-amber-50 text-amber-600 ring-amber-200" },
  low: { bar: "bg-slate-400", chip: "bg-slate-100 text-slate-600 ring-slate-200" },
};
const STATUS_STYLE = {
  open: "bg-blue-50 text-blue-700 ring-blue-200",
  in_progress: "bg-violet-50 text-violet-700 ring-violet-200",
  done: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

function initials(name) {
  return (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

export default function Tasks() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const { data: tasks = [], isLoading } = useList("tasks", "/tasks", { refetchInterval: 12000 });
  const { users } = useUsersMap();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const nameOf = (id) => users.find((u) => u.id === id)?.name || "Unassigned";
  const detail = tasks.find((t) => t.id === detailId);

  const openNew = () => { setForm({ ...empty, assigned_to: user.role !== "admin" ? user.id : "" }); setEditing(null); setOpen(true); };
  const openEdit = (t) => { setForm({ ...empty, ...t, assigned_to: t.assigned_to || "" }); setEditing(t.id); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, description: form.description, assigned_to: form.assigned_to || null, priority: form.priority, status: form.status, due_date: form.due_date };
      if (editing) await api.put(`/tasks/${editing}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editing ? "Task updated" : "Task assigned");
      setOpen(false);
      invalidate(["tasks", "dashboard"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${id}`); invalidate(["tasks", "dashboard"]); toast.success("Deleted"); };
  const quickStatus = async (t, v) => { await api.put(`/tasks/${t.id}`, { status: v }); invalidate(["tasks", "dashboard"]); };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await api.post(`/tasks/${detailId}/comments`, { text: reply.trim() });
      setReply("");
      invalidate(["tasks"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReplying(false); }
  };

  return (
    <div>
      <PageHeader title="Task Management" subtitle="Assign work, track status and reply in a thread"
        actions={<Button data-testid="new-task-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />New Task</Button>} />

      {isLoading ? <div className="text-slate-500">Loading…</div> : tasks.length === 0 ? (
        <Panel><EmptyState icon={ClipboardText} title="No tasks yet" hint="Create tasks and assign them to your team." /></Panel>
      ) : (
        <Panel className="p-0 overflow-hidden" data-testid="tasks-list">
          <div className="divide-y divide-slate-100">
            {tasks.map((t) => {
              const ps = PRIO_STYLE[t.priority] || PRIO_STYLE.medium;
              const commentCount = (t.comments || []).length;
              return (
                <div key={t.id} className="flex items-stretch group hover:bg-slate-50 transition-colors" data-testid={`task-row-${t.id}`}>
                  <div className={`w-1.5 shrink-0 ${ps.bar}`} />
                  <button onClick={() => { setDetailId(t.id); setReply(""); }} className="flex-1 min-w-0 text-left px-4 py-3.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset capitalize ${ps.chip}`}>{t.priority}</span>
                      <p className="font-semibold text-slate-900 truncate">{t.title}</p>
                      {commentCount > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><ArrowBendUpLeft size={12} />{commentCount}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-[9px] font-bold">{initials(nameOf(t.assigned_to))}</span>
                        {nameOf(t.assigned_to)}
                      </span>
                      <span className="hidden sm:inline text-slate-300">·</span>
                      <span className="hidden sm:inline">Assigned by <b className="text-slate-600 font-semibold">{t.created_by_name || nameOf(t.created_by)}</b></span>
                      {t.due_date && <span className="inline-flex items-center gap-1"><CalendarBlank size={13} />{t.due_date}</span>}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 pr-4">
                    <span className={`hidden md:inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset capitalize ${STATUS_STYLE[t.status]}`}>{t.status.replace("_", " ")}</span>
                    <Select value={t.status} onValueChange={(v) => quickStatus(t, v)}>
                      <SelectTrigger className="h-8 w-32 bg-white border-slate-300 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => openEdit(t)} className="p-1.5 text-slate-400 hover:text-blue-600" data-testid={`task-edit-${t.id}`}><PencilSimple size={16} /></button>
                    {user.role === "admin" && <button onClick={() => remove(t.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash size={16} /></button>}
                    <CaretRight size={15} className="text-slate-300" />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* DETAIL DRAWER with reply thread */}
      <Sheet open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)}>
        <SheetContent className="bg-white w-full sm:max-w-md p-0 flex flex-col" data-testid="task-detail-drawer">
          {detail && (
            <>
              <SheetHeader className="p-5 border-b border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset capitalize ${(PRIO_STYLE[detail.priority] || PRIO_STYLE.medium).chip}`}>{detail.priority}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset capitalize ${STATUS_STYLE[detail.status]}`}>{detail.status.replace("_", " ")}</span>
                </div>
                <SheetTitle className="font-heading text-xl text-left">{detail.title}</SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {detail.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{detail.description}</p>}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] text-slate-400 uppercase font-semibold">Assigned by</p><p className="font-semibold text-slate-900 mt-0.5 flex items-center gap-1.5"><UserCircle size={16} weight="duotone" className="text-blue-600" />{detail.created_by_name || nameOf(detail.created_by)}</p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] text-slate-400 uppercase font-semibold">Assigned to</p><p className="font-semibold text-slate-900 mt-0.5 flex items-center gap-1.5"><ArrowRight size={14} className="text-blue-600" />{nameOf(detail.assigned_to)}</p></div>
                  {detail.due_date && <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] text-slate-400 uppercase font-semibold">Due</p><p className="font-semibold text-slate-900 mt-0.5">{detail.due_date}</p></div>}
                </div>

                <div className="pt-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Replies</p>
                  {(detail.comments || []).length === 0 ? (
                    <p className="text-sm text-slate-400">No replies yet. Start the conversation below.</p>
                  ) : (
                    <div className="space-y-3">
                      {detail.comments.map((c) => {
                        const mine = c.user_id === user.id;
                        return (
                          <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`task-comment-${c.id}`}>
                            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${mine ? "bg-blue-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                              {!mine && <p className="text-[11px] font-bold text-blue-600 mb-0.5">{c.user_name}</p>}
                              <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                              <p className={`text-[10px] mt-0.5 ${mine ? "text-blue-100" : "text-slate-400"}`}>{fmtTime(c.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 flex items-end gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" rows={1}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  className="bg-white border-slate-300 resize-none min-h-[42px]" data-testid="task-reply-input" />
                <Button onClick={sendReply} disabled={replying || !reply.trim()} className="bg-blue-600 text-white hover:bg-blue-700 h-[42px] px-3.5" data-testid="task-reply-send"><PaperPlaneRight size={18} weight="fill" /></Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* CREATE / EDIT */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Task" : "New Task"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Title *"><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white border-slate-300" /></Field>
            <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white border-slate-300" rows={3} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Priority">
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-white border-slate-300" /></Field>
            </div>
            {user.role === "admin" && (
              <Field label="Assign to">
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="task-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-task-btn" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Save Task"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
